import { useState } from "react"
import { Link } from "@inertiajs/react"
import { Copy, Check, ArrowRight } from "lucide-react"

import { Button } from "@/components/ui/button"

// Format the dollar worth of the points (200 pts = $1, computed server-side).
function money(value) {
  const n = Number(value ?? 0)
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// D6 — the Rewards & referral card. Every figure (points, dollar worth, the
// discount / bounty / earn-rate copy) is prop-driven from the presenter; nothing
// is hardcoded. The referral link copies to the clipboard, guarded when the
// customer has no code yet.
export default function RewardsCard({ rewards }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    if (!rewards?.referralLink) return
    navigator.clipboard?.writeText(rewards.referralLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between gap-4 border-b border-border px-[22px] py-4">
        <h3 className="font-display text-[14.5px] font-bold">Rewards &amp; referral</h3>
        <Link href="/rewards" className="inline-flex items-center gap-1 text-xs font-semibold text-primary no-underline hover:underline">
          Open hub
          <ArrowRight className="size-3.5" strokeWidth={2.2} />
        </Link>
      </div>

      <div className="flex flex-col gap-3.5 px-[22px] py-[18px]">
        <div>
          <span className="font-display text-2xl font-bold tracking-[-0.4px] text-foreground">
            {(rewards?.points ?? 0).toLocaleString("en-US")}
          </span>
          <span className="ml-1.5 text-[13px] text-ink-3">points · worth {money(rewards?.dollarsValue)}</span>
        </div>

        <p className="text-[13px] leading-snug text-ink-3">
          Earn {rewards?.pointsPerDollar ?? 2} points per $1 on every order.
        </p>

        {/* P8-H7 "Muster" — the referral-link preview wraps in the starved
            861–1023 band. The Copy button is a fixed-width DS control, so in a
            ~217px column the link span collapsed to a few characters ("http:").
            Giving the span the full line and dropping the button beneath it is the
            same treatment the Rewards hub's own link box gets. */}
        <div
          data-testid="profile-referral-box"
          className="flex items-center gap-2 rounded-[10px] border border-border bg-secondary px-3 py-2 min-[861px]:max-[1024px]:flex-wrap min-[861px]:max-[1024px]:gap-y-2"
        >
          <span
            data-testid="profile-referral-link"
            className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap text-[12.5px] text-ink-3 min-[861px]:max-[1024px]:basis-full"
          >
            {rewards?.referralLink ?? "Your referral link will appear here."}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={copy}
            disabled={!rewards?.referralLink}
            aria-label="Copy referral link"
          >
            {copied ? (
              <>
                <Check className="size-3.5" strokeWidth={2.2} />
                Copied
              </>
            ) : (
              <>
                <Copy className="size-3.5" strokeWidth={2} />
                Copy link
              </>
            )}
          </Button>
        </div>

        <p className="text-xs leading-snug text-ink-3">
          Friends get {rewards?.friendDiscountPct ?? 5}% off — you earn{" "}
          {(rewards?.referralBountyPoints ?? 2000).toLocaleString("en-US")} points when they place
          their first order.
        </p>
      </div>
    </div>
  )
}
